import {
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	RouteParametersFromProps,
	connectToRouter
} from "client/Navigation";
import {
	FunctionComponent,
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { AddressBookContactSource } from "./Overview/Overview";
import {
	addressBookRouting,
	convertToSourcePath,
	decodeIdFromRouting,
	tryParseContactSource
} from "./routing";
import { Details } from "./Details/Details";
import {
	ensureNotNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	BusyIndicator,
	ContextActionBar,
	FloatingActionButton,
	IconButtonWithTitle,
	NoResults,
	PageContentTitle,
	ToolbarAction
} from "client/Components";
import styles from "./DetailsPage.module.less";
import {
	ContactDetailsModel,
	ContactKind,
	ContactSource,
	ExchangeContactDetailsModel,
	deletePersonalContact
} from "client/Api/Email/contacts";
import { localize } from "client_legacy/ndaw/culture";
import { useFetchContactDetails } from "./Details/useFetchContactDetails";
import { never } from "client_legacy/ndaw/errors";
import { ActionKind } from "client_legacy/ndaw/actions";
import { useEditContactDialog } from "./Edit/EditDialog";
import { useDeleteContactConfirmationDialog } from "./DeleteContactConfirmationDialog";
import { getComputedDisplayName } from "./common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { TitleBarBackButton } from "client/Features/Layout/TitleBarBackButton/TitleBarBackButton";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type DetailsPageProps = {
	source: AddressBookContactSource;
	id: string;
	onNavigate: NavigationCallback;
	department?: string;
	officeLocation?: string;
};

export const DetailsPage = (props: DetailsPageProps): ReactElement => {
	const {
		source,
		id: idFromProps,
		onNavigate,
		department,
		officeLocation
	} = props;

	const id: string = decodeIdFromRouting(source, idFromProps);

	const [isDeletingContact, setIsDeletingContact] = useState<boolean>(false);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const navigateToOverview = useCallback(
		(): void => {
			const sourcePath: string | undefined = convertToSourcePath(source);

			onNavigate(addressBookRouting.overview({
				routeParams: {
					source: ensureNotNil(sourcePath)
				},
				queryParams: {
					department,
					officeLocation
				}
			}));
		},
		[
			onNavigate,
			source,
			department,
			officeLocation
		]
	);

	const {
		state,
		reload
	} = useFetchContactDetails(source, id);

	const [editContactDialog, openEditContactDialog] = useEditContactDialog();
	const [deleteConfirmationDialog, opeDeleteConfirmationDialog] = useDeleteContactConfirmationDialog();

	const renderContent = (): ReactElement | null => {
		switch (state.kind) {
			case "loading":
				return <BusyIndicator />;
			case "error":
				if (state.errorKind === "not-found"
					&& source === ContactSource.Workspace
				) {
					return (
						<NoResults
							icon="user"
							title={localize("_UserDoesNotExist")}
							message={localize("_TheUserIsDeletedOrInactive")}
							trackingId="loading-error"
							height="fill"
						/>
					);
				} else {
					return (
						<NoResults
							icon="gear"
							title={localize("_FailedToRetrieveTheContactDetails")}
							message={localize("_WeWereUnableToRetrieveContactDetails")}
							onRetry={reload}
							trackingId="loading-error"
							height="fill"
						/>
					);
				}
			case "loaded":
				return (
					<>
						<Details
							contact={state.contact}
						/>
						<AiAssistantFabSpacing />
					</>
				);
			default:
				never(state);
		}
	};

	const editPersonalContactHandler = useCallback(
		async (contact: ExchangeContactDetailsModel): Promise<void> => {
			const editResult: TryResult<string> = await openEditContactDialog({
				exchangeContactId: contact.id
			});

			if (editResult.wasSuccessful) {
				reload();
			}
		},
		[openEditContactDialog, reload]
	);

	const deletePersonalContactHandler = useCallback(
		async (contact: ExchangeContactDetailsModel): Promise<void> => {
			const confirmationResult: boolean = await opeDeleteConfirmationDialog({
				contactDisplayName: getComputedDisplayName(contact).computedDisplayName,
				trackingId: `delete-contact-confirmation-dialog`
			});

			if (!confirmationResult) {
				return;
			}

			try {
				setIsDeletingContact(true);
				await deletePersonalContact(contact.id, globalCancellation);
				navigateToOverview();
				alertManager.showSuccess(localize("_SuccessfullyDeletedTheContact"));
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}
				setIsDeletingContact(false);
				alertManager.showError(localize("_FailedToDeleteTheContact"));
				console.error(e);
			}
		},
		[
			alertManager,
			globalCancellation,
			navigateToOverview,
			opeDeleteConfirmationDialog
		]
	);

	const editableContact: ExchangeContactDetailsModel | undefined =
		(source === ContactSource.Personal
			&& state.kind === "loaded"
			&& state.contact.kind === ContactKind.Exchange
		)
			? state.contact
			: undefined;

	const actions = useMemo(
		(): readonly ContactDetailsAction[] => {
			return getActions({
				editPersonalContact: editPersonalContactHandler,
				deletePersonalContact: deletePersonalContactHandler
			});
		},
		[
			deletePersonalContactHandler,
			editPersonalContactHandler
		]
	);

	const actionContext = useMemo(
		(): ContactActionContext => {
			return {
				isResponsive,
				canEditOrDeleteContact: isNotNil(editableContact),
				contact: editableContact
			};
		},
		[
			isResponsive,
			editableContact
		]
	);

	return (
		<>
			<TitleBarBackButton
				onBack={navigateToOverview}
			/>

			{isMobile && (
				<ContextActionBar
					actions={actions}
					context={actionContext}
					hasBorderBottom
					trackingId="global-action-bar"
				/>
			)}

			<article
				{...setTrackingId("details-page")}
				className={styles.root}
			>
				<div className={styles.header}>
					{!isResponsive && (
						<IconButtonWithTitle
							title={localize(sourceToTitleLocalizationKey[source])}
							icon="chevron-left"
							onClick={navigateToOverview}
							trackingId="page-content-back"
						/>
					)}
					<PageContentTitle
						{...(!isMobile
							? {
								actions,
								actionContext
							}
							: {
								actions: undefined
							}
						)}
						title={localize("_ContactDetails")}
					/>
				</div>
				{isDeletingContact && <BusyIndicator />}
				{renderContent()}
				{isNotNil(editableContact) && (
					<>
						<div className="preserve-space-for-mobile-plus-button" />
						<FloatingActionButton
							icon="pen"
							onClick={() => editPersonalContactHandler(editableContact)}
							trackingId="fab-edit-contact"
						/>
					</>
				)}

				{editContactDialog}
				{deleteConfirmationDialog}
			</article>
		</>
	);
};
DetailsPage.displayName = "DetailsPage";

export type DetailsPageRouteParameters = RouteParametersFromProps<
	DetailsPageProps,
	| "source"
	| "id"
>;

export type DetailsPageQueryParameters = QueryParametersFromProps<
	DetailsPageProps,
	| "department"
	| "officeLocation"
>;

export const DetailsPageForRouter: FunctionComponent = connectToRouter(
	DetailsPage,
	{
		"source": {
			isRequired: true,
			tryParse: tryParseContactSource
		},
		"id": { isRequired: true },
		"department": {
			isRequired: false,
			type: "query"
		},
		"officeLocation": {
			isRequired: false,
			type: "query"
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

const sourceToTitleLocalizationKey: { [key in AddressBookContactSource]: LocalizationKey } = {
	[ContactSource.Personal]: "_PersonalContacts",
	[ContactSource.Gal]: "_OrganisationContacts",
	[ContactSource.Workspace]: "_WorkspaceContacts",
};

type ContactActionContext = {
	canEditOrDeleteContact: boolean;
	contact?: ContactDetailsModel;
	isResponsive: boolean;
};

type ContactActionHandlers = {
	editPersonalContact(contact: ExchangeContactDetailsModel): void;
	deletePersonalContact(contact: ExchangeContactDetailsModel): void;
};

type ContactDetailsAction = ToolbarAction<ContactActionContext>;

function getActions(handlers: ContactActionHandlers): readonly ContactDetailsAction[] {
	const {
		editPersonalContact,
		deletePersonalContact
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			icon: "pen",
			isPrimary: true,
			canExecute: (ctx) => !ctx.isResponsive
				&& ctx.canEditOrDeleteContact,
			execute: (ctx) => editPersonalContact(ensureNotNil(ctx.contact) as ExchangeContactDetailsModel)
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (ctx) => ctx.canEditOrDeleteContact,
			execute: (ctx) => deletePersonalContact(ensureNotNil(ctx.contact) as ExchangeContactDetailsModel)
		}
	];
}